/**
 * Static context the *surface* knows and the guest never typed — today the
 * standalone widget's URL parameters (room number, floor, board…). It is fixed
 * for the whole page load, so it lives here as a module singleton instead of
 * being threaded through every message-sending signature; ChatService reads it
 * straight off this object when it builds a request.
 *
 * The engine sanitises it again server-side (it is caller-supplied and lands in
 * the system prompt); the caps here just keep an absurd URL from ever leaving
 * the browser.
 */

const MAX_KEYS = 12;
const MAX_KEY_LENGTH = 48;
const MAX_VALUE_LENGTH = 160;

/** Query params the widget consumes itself — never guest context. */
const RESERVED_KEYS = new Set([
  "a_id",
  "a_name",
  "assistant",
  "islocal",
  "lang",
  "language",
  "mode",
]);

let current: Record<string, string> = {};

const clean = (value: string, max: number) =>
  value.replace(/\s+/g, " ").trim().slice(0, max);

const SessionContext = {
  set(next: Record<string, any> | null | undefined) {
    const result: Record<string, string> = {};

    Object.entries(next ?? {}).forEach(([key, value]) => {
      if (Object.keys(result).length >= MAX_KEYS) return;
      if (value === null || value === undefined) return;
      if (typeof value === "object") return;

      const safeKey = clean(String(key), MAX_KEY_LENGTH);
      const safeValue = clean(String(value), MAX_VALUE_LENGTH);
      if (!safeKey || !safeValue) return;

      result[safeKey] = safeValue;
    });

    current = result;
  },

  get(): Record<string, string> {
    return current;
  },

  isEmpty() {
    return Object.keys(current).length === 0;
  },

  /**
   * Everything in a query string that the widget does not consume itself.
   * `?a_id=…&room=214&floor=2` yields `{ room: "214", floor: "2" }`.
   */
  fromSearchParams(params: URLSearchParams): Record<string, string> {
    const result: Record<string, string> = {};

    params.forEach((value, key) => {
      if (RESERVED_KEYS.has(key.toLowerCase())) return;
      result[key] = value;
    });

    return result;
  },
};

export default SessionContext;
